import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/da2kvjbd3r93mn1k3b0g';

type Status = 'idle' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }
    if (!honeypot) formData.delete('website_alt');

    const name = String(formData.get('name') || '').trim();
    const email = String(formData.get('email') || '').trim();
    const message = String(formData.get('message') || '').trim();
    if (!name || !email || !message) {
      setFormError('请填写姓名、邮箱和留言内容后再提交。');
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setFormError('');
    try {
      const body = new URLSearchParams(formData as unknown as Record<string, string>).toString();
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setFormError(serverMsg && serverMsg !== 'OK' ? serverMsg : '提交失败，请稍后再试。');
        setStatus('error');
      }
    } catch {
      setFormError('网络异常，请检查网络后重试。');
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center rounded-3xl border border-primary-200 bg-background-50 p-10 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
          <i className="ri-check-line text-3xl" aria-hidden="true" />
        </span>
        <h2 className="mt-5 font-heading text-2xl text-foreground-950">提交成功！</h2>
        <p className="mt-2 text-sm text-foreground-700">感谢你的留言，我们会在 3 个工作日内回复你。</p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
        >
          再发一条
        </button>
      </div>
    );
  }

  return (
    <form
      id="contact-form"
      data-readdy-form="歪歪漫画联系我们表单"
      onSubmit={submit}
      className="rounded-3xl border border-background-200/70 bg-background-50 p-6 md:p-8"
    >
      <h2 className="font-heading text-2xl text-foreground-950">给我们留言</h2>
      <p className="mt-2 text-sm text-foreground-600">带 * 为必填项，我们会认真阅读每一条留言。</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="cf-name" className="text-sm font-bold text-foreground-800">您的称呼 *</label>
          <input
            id="cf-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼您"
            className="rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="cf-email" className="text-sm font-bold text-foreground-800">邮箱地址 *</label>
          <input
            id="cf-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="cf-subject" className="text-sm font-bold text-foreground-800">留言主题</label>
        <input
          id="cf-subject"
          name="subject"
          type="text"
          placeholder="如：投稿合作 / 作品建议 / 咨询追更"
          className="rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400"
        />
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="cf-message" className="text-sm font-bold text-foreground-800">留言内容 *</label>
        <textarea
          id="cf-message"
          name="message"
          required
          maxLength={500}
          rows={6}
          placeholder="想说的话，或想投稿的作品简介（500 字以内）"
          className="resize-none rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400"
        />
      </div>

      <div className="is-skip" aria-hidden="true">
        <label htmlFor="cf-website">网站</label>
        <input
          id="cf-website"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          defaultValue=""
        />
      </div>

      {status === 'error' && formError && (
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-primary-100 px-4 py-3 text-sm text-primary-800" role="alert">
          <i className="ri-error-warning-line mt-0.5" aria-hidden="true" />
          <span>{formError}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
      >
        {submitting ? (
          <>
            <i className="ri-loader-4-line animate-spin" aria-hidden="true" /> 提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" aria-hidden="true" /> 发送留言
          </>
        )}
      </button>
    </form>
  );
}